/**
 * Client-side OG / Twitter meta + document title for SPA article routes.
 * Crawlers that execute JS (e.g. some social bots) can read these; static crawlers still see index.html defaults.
 */

const DEFAULT = {
  title: 'Neural News — Enterprise News Platform',
  description:
    'Premium journalism, breaking news, and in-depth analysis from around the world.',
  ogTitle: 'Neural News — Enterprise News Platform',
  ogDescription:
    'Premium journalism, breaking news, and in-depth analysis from around the world.',
  ogType: 'website',
};

function excerptFromArticle(article, maxLen = 160) {
  const raw = article.subtitle || article.content || '';
  const text = raw
    .replace(/<[^>]+>/g, ' ')
    .replace(/[#*_`\[\]()]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) return DEFAULT.description;
  return text.length <= maxLen ? text : `${text.slice(0, maxLen - 1)}…`;
}

function upsertMeta(attrName, key, content) {
  if (content == null || content === '') return;
  const selector = `meta[${attrName}="${key}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attrName, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLinkCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function clearAlternates() {
  document.head.querySelectorAll('link[rel="alternate"][data-source="article-meta"]').forEach((n) => n.remove());
}

function upsertAlternate(hreflang, href) {
  if (!href) return;
  const el = document.createElement('link');
  el.setAttribute('rel', 'alternate');
  el.setAttribute('hreflang', hreflang);
  el.setAttribute('href', href);
  el.setAttribute('data-source', 'article-meta');
  document.head.appendChild(el);
}

/**
 * Describe an article's syndication, or null when it is our own reporting.
 *
 * An article is syndicated if the auto-publisher flagged it, or — for
 * anything published before the flag existed — if it carries a source
 * URL at all. Shared by the SEO layer and the article page so the
 * canonical link and the visible credit can never disagree.
 *
 * @param {object} article - API article shape
 * @returns {{url: string, publication: string, author: string|null}|null}
 */
export function syndicationSource(article) {
  if (!article) return null;
  const url = (article.source_url || '').trim();
  const flagged = article.is_syndicated === true || !!url;
  if (!flagged || !/^https?:\/\//i.test(url)) return null;

  let publication = (article.source_publication || article.source_feed_name || '').trim();
  if (!publication) {
    try {
      publication = new URL(url).hostname.replace(/^www\./, '');
    } catch (_) {
      publication = 'the original publisher';
    }
  }
  return {
    url,
    publication,
    author: (article.source_author || '').trim() || null,
  };
}

/**
 * @param {object} article - API article shape
 * @param {{ url: string }} options - our own page URL (absolute)
 */
export function applyArticleSEO(article, { url }) {
  if (!article) return;

  // Use stored overrides if present, else auto-generate
  const ogTitleRaw = article.og_title || article.title;
  const ogDescRaw = article.og_description || excerptFromArticle(article);
  const ogImage = article.og_image || article.featured_image || '';

  // Syndicated articles are canonicalised to the ORIGINAL publisher: our
  // page is a summary card pointing at their reporting, and search
  // engines must credit and rank them, not us. `url` stays our own page
  // for og:url and hreflang, which describe *this* page.
  const syndication = syndicationSource(article);
  const canonicalHref = syndication ? syndication.url : (article.canonical_url || url);
  const pageUrl = url;

  document.title = `${ogTitleRaw} | Neural News`;

  upsertMeta('name', 'description', ogDescRaw);

  upsertMeta('property', 'og:title', ogTitleRaw);
  upsertMeta('property', 'og:description', ogDescRaw);
  upsertMeta('property', 'og:type', 'article');
  upsertMeta('property', 'og:url', pageUrl);
  if (ogImage) {
    upsertMeta('property', 'og:image', ogImage);
    upsertMeta('property', 'og:image:alt', ogTitleRaw);
  }

  upsertMeta('name', 'twitter:card', ogImage ? 'summary_large_image' : 'summary');
  upsertMeta('name', 'twitter:title', ogTitleRaw);
  upsertMeta('name', 'twitter:description', ogDescRaw);
  if (ogImage) {
    upsertMeta('name', 'twitter:image', ogImage);
  }

  upsertLinkCanonical(canonicalHref);

  // hreflang alternates — explicit translations on the article + x-default
  clearAlternates();
  const translations = Array.isArray(article.translations) ? article.translations : [];
  translations.forEach((t) => {
    if (t.lang && t.url) upsertAlternate(t.lang, t.url);
  });
  if (article.language) {
    upsertAlternate(article.language, pageUrl);
  }
  upsertAlternate('x-default', pageUrl);
}

export function resetPublicSEO() {
  if (typeof window === 'undefined') return;

  const pageUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;

  document.title = DEFAULT.title;

  upsertMeta('name', 'description', DEFAULT.description);
  upsertMeta('property', 'og:title', DEFAULT.ogTitle);
  upsertMeta('property', 'og:description', DEFAULT.ogDescription);
  upsertMeta('property', 'og:type', DEFAULT.ogType);
  upsertMeta('property', 'og:url', pageUrl);

  const ogImage = document.head.querySelector('meta[property="og:image"]');
  if (ogImage) ogImage.remove();
  const ogImageAlt = document.head.querySelector('meta[property="og:image:alt"]');
  if (ogImageAlt) ogImageAlt.remove();

  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', DEFAULT.ogTitle);
  upsertMeta('name', 'twitter:description', DEFAULT.ogDescription);
  const twImg = document.head.querySelector('meta[name="twitter:image"]');
  if (twImg) twImg.remove();

  const canonical = document.head.querySelector('link[rel="canonical"]');
  if (canonical) {
    canonical.setAttribute('href', pageUrl);
  }
  clearAlternates();
}
